import { get } from "@src/appV2/api/api";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const TIMEKEEPING_VALIDATION_URL = "/shifts/record_timekeeping_action/validate";

const validateTimekeepingActionQuerySchema = z.object({
  editedClockIn: z.string().optional(),
  editedClockOut: z.string().optional(),
});

export const validateTimekeepingActionErrorSchema = z.object({
  statusCode: z.number(),
  error: z.string(),
  message: z.string(),
});

export type ValidateTimekeepingActionError = z.infer<typeof validateTimekeepingActionErrorSchema>;

export interface UseValidateTimekeepingActionParams {
  shiftId: string;
  editedClockIn?: string;
  editedClockOut?: string;
}

/**
 * Validates that edited clock-in/out times don't overlap with other shifts.
 * Uses GET /api/shifts/record_timekeeping_action/validate/:shiftId.
 * Returns 200 on success, throws 400 BadRequest when overlap is detected.
 */
export function useValidateTimekeepingAction(): UseMutationResult<
  void,
  AxiosError<ValidateTimekeepingActionError>,
  UseValidateTimekeepingActionParams
> {
  return useMutation({
    mutationFn: async (params: UseValidateTimekeepingActionParams) => {
      const { shiftId, ...queryParams } = params;

      await get({
        url: `${TIMEKEEPING_VALIDATION_URL}/${shiftId}`,
        queryParams,
        requestSchema: validateTimekeepingActionQuerySchema,
        responseSchema: z.unknown(),
      });
    },
  });
}
